import {
  AccountInfo,
  BrowserAuthError,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';
import { ENTRA_CONFIG } from '../../config/bootstrap';

let pca: PublicClientApplication | null = null;
let initPromise: Promise<PublicClientApplication | null> | null = null;
let loginRedirectStarted = false;

const isInteractionInProgress = (error: unknown): boolean => {
  return error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress';
};

const hasMsalRedirectParams = (): boolean => {
  const query = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return Boolean(
    query.get('code') || query.get('error') || hash.get('code') || hash.get('error')
  );
};

const clearStaleInteraction = (): void => {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (key && key.includes('interaction.status')) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    /* ignore */
  }
};

const loginScopes = (): string[] => {
  const scopes = ['openid', 'profile', 'email'];
  if (ENTRA_CONFIG.API_SCOPE) {
    scopes.push(ENTRA_CONFIG.API_SCOPE);
  }
  return scopes;
};

const tokenScopes = (): string[] => {
  if (ENTRA_CONFIG.API_SCOPE) {
    return [ENTRA_CONFIG.API_SCOPE];
  }
  return ['openid', 'profile', 'email'];
};

export const getMsal = (): PublicClientApplication => {
  if (!ENTRA_CONFIG.CLIENT_ID) {
    throw new Error('site.portalClientId is not configured; set it in the site seed and run branding');
  }
  if (!pca) {
    pca = new PublicClientApplication({
      auth: {
        clientId: ENTRA_CONFIG.CLIENT_ID,
        authority: ENTRA_CONFIG.AUTHORITY,
        redirectUri: `${window.location.origin}/auth/callback`,
        postLogoutRedirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: 'sessionStorage',
      },
    });
  }
  return pca;
};

export const initMsal = (): Promise<PublicClientApplication | null> => {
  if (!ENTRA_CONFIG.CLIENT_ID) {
    return Promise.resolve(null);
  }
  if (!initPromise) {
    initPromise = (async () => {
      const instance = getMsal();
      await instance.initialize();
      // Must run on every page load before loginRedirect / acquireTokenRedirect.
      const result = await instance.handleRedirectPromise();
      if (result?.account) {
        instance.setActiveAccount(result.account);
      } else {
        const account = activeAccount(instance);
        if (account) {
          instance.setActiveAccount(account);
        }
        if (!hasMsalRedirectParams()) {
          clearStaleInteraction();
        }
      }
      return instance;
    })();
  }
  return initPromise;
};

const activeAccount = (instance: PublicClientApplication): AccountInfo | null => {
  return instance.getActiveAccount() || instance.getAllAccounts()[0] || null;
};

export const isEntraAuthenticated = async (): Promise<boolean> => {
  if (!ENTRA_CONFIG.CLIENT_ID) {
    return false;
  }
  const instance = await initMsal();
  return Boolean(instance && activeAccount(instance));
};

export const loginEntra = async (): Promise<void> => {
  const instance = await initMsal();
  if (!instance) {
    throw new Error('site.portalClientId is not configured; set it in the site seed and run branding');
  }
  if (activeAccount(instance) || loginRedirectStarted) {
    return;
  }
  loginRedirectStarted = true;
  try {
    await instance.loginRedirect({ scopes: loginScopes() });
  } catch (error) {
    loginRedirectStarted = false;
    if (isInteractionInProgress(error)) {
      return;
    }
    throw error;
  }
};

export const handleEntraRedirect = async (): Promise<void> => {
  await initMsal();
};

export const getEntraAccessToken = async (): Promise<string | null> => {
  const instance = await initMsal();
  if (!instance) {
    return null;
  }
  const account = activeAccount(instance);
  if (!account) {
    return null;
  }
  try {
    const result = await instance.acquireTokenSilent({
      account,
      scopes: tokenScopes(),
    });
    return result.accessToken || null;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      try {
        await instance.acquireTokenRedirect({
          account,
          scopes: tokenScopes(),
        });
      } catch (redirectError) {
        if (!isInteractionInProgress(redirectError)) {
          throw redirectError;
        }
      }
      return null;
    }
    throw error;
  }
};

export const getEntraDisplayIdentity = async (): Promise<string | null> => {
  const instance = await initMsal();
  if (!instance) {
    return null;
  }
  const account = activeAccount(instance);
  if (!account) {
    return null;
  }
  const claims = (account.idTokenClaims || {}) as Record<string, unknown>;
  const name = typeof claims.name === 'string' ? claims.name : '';
  const email =
    (typeof claims.email === 'string' && claims.email) ||
    (typeof claims.preferred_username === 'string' && claims.preferred_username) ||
    account.username;
  if (name && email && name !== email) {
    return `${name} (${email})`;
  }
  return email || name || null;
};

export const signOutEntra = async (): Promise<void> => {
  const instance = await initMsal();
  if (!instance) {
    return;
  }
  const account = activeAccount(instance);
  await instance.logoutRedirect({ account: account || undefined });
};
